import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchDisruptionRosterPage } from '@/lib/disruptions/api';
import { useDisruptionTrips, useTransferBoard } from '@/lib/disruptions/use-disruptions';
import type {
  DisruptionPnrGroupRow,
  DisruptionTripsPage,
  ITransferBoard,
} from '@/lib/disruptions/types';
import { buildTransportDesk, type TransportDeskState } from './transport-desk-model';

// Data hook for the Transport desk: the roster walk + the transfer board + the
// disruption trips read, folded into the desk model. The walk shares the Hotels
// desk's EXACT query key + shape (`['disruptions', urn, 'roster', 'desk-walk']`)
// so the two desks share one cache entry and every existing invalidation — the
// WS CaseChanged/progress pushes, allocate/voucher/transfer onSuccess — refreshes
// both for free. Realtime = WS: board + trips are seeded once and patched by
// apply-event; no interval poll anywhere.

const DESK_MAX_PAGES = 20;

export interface TransportDeskQuery {
  desk: TransportDeskState | null;
  board: ITransferBoard | null | undefined;
  trips: DisruptionTripsPage | null | undefined;
  isLoading: boolean;
  isError: boolean;
  boardLoading: boolean;
  boardError: boolean;
  tripsLoading: boolean;
  /** True when the roster had more pages than the walk cap — counts are a floor. */
  truncated: boolean;
  refetch: () => void;
}

export function useTransportDesk(
  disruptionUrn: string | undefined,
  enabled = true,
): TransportDeskQuery {
  const qc = useQueryClient();
  const walk = useQuery<{ rows: DisruptionPnrGroupRow[]; truncated: boolean }>({
    // SAME key + payload shape as hotel-desk/use-hotel-desk.ts — one shared walk.
    queryKey: ['disruptions', disruptionUrn, 'roster', 'desk-walk'],
    enabled: !!disruptionUrn && enabled,
    staleTime: 15_000,
    queryFn: async () => {
      const rows: DisruptionPnrGroupRow[] = [];
      let cursor: string | undefined;
      let truncated = false;
      for (let page = 0; page < DESK_MAX_PAGES; page += 1) {
        const res = await fetchDisruptionRosterPage(disruptionUrn!, {
          ...(cursor ? { cursor } : {}),
        });
        rows.push(...(res.items ?? []));
        cursor = res.nextCursor ?? undefined;
        if (!cursor) break;
        if (page === DESK_MAX_PAGES - 1) truncated = true;
      }
      return { rows, truncated };
    },
  });
  const board = useTransferBoard(disruptionUrn, enabled);
  const trips = useDisruptionTrips(disruptionUrn, enabled);

  const desk = useMemo(
    () =>
      walk.data ? buildTransportDesk(walk.data.rows, board.data, trips.data?.trips) : null,
    [walk.data, board.data, trips.data],
  );

  return {
    desk,
    board: board.data,
    trips: trips.data,
    isLoading: walk.isLoading,
    isError: walk.isError,
    boardLoading: board.isLoading,
    boardError: board.isError,
    tripsLoading: trips.isLoading,
    truncated: walk.data?.truncated ?? false,
    refetch: () => {
      void qc.invalidateQueries({ queryKey: ['disruptions', disruptionUrn, 'roster'] });
      void qc.invalidateQueries({ queryKey: ['disruptions', disruptionUrn, 'transfers'] });
      void qc.invalidateQueries({ queryKey: ['disruptions', disruptionUrn, 'trips'] });
    },
  };
}
